import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'src/redesign/components/Button';
import { InputOtp } from 'primereact/inputotp';
import styles from './EmailVerification.module.scss';
import { API_INSTANCE } from 'src/features/utils';
import { InvisibleRecaptchaHandle } from 'src/redesign/components/InvisibleRecaptcha';

type EmailVerificationProps = {
  email: string;
  onVerified: () => void;
  onCancel: () => void;
  invisibleRecaptchaRef: React.RefObject<InvisibleRecaptchaHandle>;
  recaptchaLoaded: boolean;
};

export const EmailVerification = ({
  email,
  onVerified,
  onCancel,
  invisibleRecaptchaRef,
  recaptchaLoaded,
}: EmailVerificationProps) => {
  const [verificationCodeInput, setVerificationCodeInput] = useState('');
  const [cooldownActive, setCooldownActive] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(20);
  const [verifying, setVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [codeId, setCodeId] = useState<string | null>(null);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const CODE_LENGTH = 6;

  const sendCode = useCallback(async () => {
    if (!recaptchaLoaded) {
      setVerificationStatus('Security verification is loading. Please wait.');
      return;
    }

    try {
      setVerificationStatus('');

      // Execute invisible reCAPTCHA
      const captchaToken = await invisibleRecaptchaRef?.current?.execute();

      if (!captchaToken) {
        setVerificationStatus('Failed to verify reCAPTCHA. Please try again.');
        return;
      }

      const { data } = await API_INSTANCE.post('/api/v1/auth/code-sender', {
        email,
        'g-recaptcha-response': captchaToken,
      });

      setCodeId(data.codeId);
      setCooldownActive(true);
      setCooldownTime(20);

      // Clear any existing timer
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }

      // Start cooldown timer
      cooldownTimerRef.current = setInterval(() => {
        setCooldownTime((prev) => {
          if (prev <= 1) {
            clearInterval(cooldownTimerRef.current!);
            setCooldownActive(false);
            return 20;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Error sending verification code:', err);
      setVerificationStatus('Failed to send code. Please try again.');
      invisibleRecaptchaRef?.current?.reset();
    }
  }, [email, invisibleRecaptchaRef, recaptchaLoaded]);

  useEffect(() => {
    sendCode();
    // Cleanup timer on unmount
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, [sendCode]);

  const onCodeChange = (e: any) => {
    const value = e.value;
    setVerificationCodeInput(value);
  };

  const verifyCode = useCallback(async () => {
    if (!verificationCodeInput || verificationCodeInput.length !== CODE_LENGTH) {
      setVerificationStatus(`Please enter all ${CODE_LENGTH} digits`);
      return;
    }

    if (!codeId) {
      setVerificationStatus('No verification code sent. Please try resending.');
      return;
    }

    setVerifying(true);
    setVerificationStatus('');

    try {
      // Verify the code
      const { data: verificationData } = await API_INSTANCE.post(
        '/api/v1/dashboard/accounts/verify-notification-email-code-verification',
        {
          email,
          code_verification: { code: verificationCodeInput, id: codeId },
        },
      );

      // Complete the verification
      await API_INSTANCE.post(
        '/api/v1/dashboard/accounts/complete-notification-email-verification',
        {
          notification_email: email,
          verificationCode: verificationData.verificationCode,
        },
      );

      onVerified();
    } catch (error: any) {
      console.error('Error verifying code:', error);
      const errorMessage = error?.response?.data?.message || 'Invalid code. Please try again.';
      setVerificationStatus(errorMessage);
      setVerifying(false);
    }
  }, [codeId, email, verificationCodeInput, onVerified]);

  return (
    <div className={styles.pinFormWrapper}>
      <p className={styles.instructions}>
        Please enter the 6-digit code sent to: <strong>{email}</strong>
      </p>

      <div className={styles.pinForm}>
        <InputOtp
          value={verificationCodeInput}
          onChange={onCodeChange}
          length={6}
          className={styles.inputOtp}
          disabled={verifying}
        />

        {verifying && <p className={styles.verifyingMessage}>Verifying...</p>}
        {verificationStatus && <small className={styles.errorMessage}>{verificationStatus}</small>}

        <div className={styles.buttonContainer}>
          {cooldownActive ? (
            <Button priority="secondary" size="small" disabled className={styles.resendButton}>
              {`Resend in ${cooldownTime}s`}
            </Button>
          ) : (
            <Button
              priority="secondary"
              size="small"
              onClick={sendCode}
              className={styles.resendButton}
            >
              Resend Code
            </Button>
          )}

          <Button
            priority="primary"
            size="small"
            onClick={verifyCode}
            disabled={verifying || verificationCodeInput.length !== 6}
            className={styles.submitButton}
          >
            Verify Email
          </Button>
        </div>
      </div>

      <div className={styles.goBackContainer}>
        <Button priority="secondary" size="small" onClick={onCancel} className={styles.goBackLink}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
